import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReactFlow } from "reactflow";

import { hideComponentPropsPanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { removeStepFromContext, deleteContextVariable } from "redux/actions/flowDesigner/editorContextActionCreator";

import { COMMON_COMPONENT_TYPES, COMPONENT_PROPS_TAB_PREFIX } from "constants/flowDesigner";

import useGetBranchNodes from "./useGetBranchNodes";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { setRecentlyDeletedBranchNodeId } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { flushSync } from "react-dom";
import { FlowStateContextApi } from "layouts/Flow Designer/context";

const useDeleteNode = () => {
   const { getChildNodeIdsBeforeMergeNode } = useGetBranchNodes();
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const reactFlowInstance = useReactFlow();
   const { getNode, getNodes, setNodes, deleteElements } = reactFlowInstance;
   const dispatch = useDispatch();
   const { deleteLayoutTab, deleteMultipleLayoutTabs } = useFlexLayout();
   const { takeSnapshot } = useContext(FlowStateContextApi);

   const hideSelectedNodePanel = () => {
      dispatch(hideComponentPropsPanel());
   };

   const removeDataFromContext = (data) => {
      const flowName = currentSavedFlow?.name;
      if (data?.type === COMMON_COMPONENT_TYPES.VARIABLES) {
         dispatch(deleteContextVariable(data?.componentKey, flowName));
      }
      dispatch(removeStepFromContext(data?.componentKey, flowName));
   };

   const deleteNode = (nodeId, data) => {
      try {
         takeSnapshot?.();
         const nodeToDelete = getNode(nodeId);
         const isDraggedNode = data?.draggedNode;
         const deletesBranch =
            data?.type === COMMON_COMPONENT_TYPES.SELECTION ||
            data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL;
         const childNodeIds = deletesBranch
            ? getChildNodeIdsBeforeMergeNode(nodeToDelete)
            : [];
          hideSelectedNodePanel();

         if (!isDraggedNode) {
            removeDataFromContext(data);
         }

         if (data?.type === COMMON_COMPONENT_TYPES.VARIABLES && data?.componentKey) {
            const referencePrefix = `variables.${data.componentKey}.`;
            setNodes(
               getNodes().map((node) => {
                  if (!node.data?.variable_reference?.startsWith(referencePrefix)) return node;
                  const updatedData = { ...node.data };
                  delete updatedData.variable_reference;
                  return { ...node, data: updatedData };
               })
            );
         }

         if (data?.type === COMMON_COMPONENT_TYPES.SELECTION || data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL) {
            flushSync(() => dispatch(setRecentlyDeletedBranchNodeId(nodeId)));

            childNodeIds.forEach((id) => {
               const childNode = getNode(id);
               if (childNode) {
                  if (!isDraggedNode) {
                     removeDataFromContext(childNode.data);
                  }
               }
            });

            if (childNodeIds.length) {
               const nodeToDeleteTabId = `${COMPONENT_PROPS_TAB_PREFIX}${nodeId}`;
               const childNodesTabIds = childNodeIds.map((id) => `${COMPONENT_PROPS_TAB_PREFIX}${id}`);
               const componentTabIdsToDelete = [nodeToDeleteTabId, ...childNodesTabIds];
               deleteMultipleLayoutTabs(componentTabIdsToDelete);
               deleteElements({
                  nodes: [{ id: nodeId }, ...childNodeIds.map((id) => ({ id: id }))]
               });
               return;
            }
         }

         const componentTabId = `${COMPONENT_PROPS_TAB_PREFIX}${nodeId}`;
         deleteLayoutTab(componentTabId);
         deleteElements({ nodes: [{ id: nodeId }] });
      } catch (error) {
         console.log(error);
      }

      return;
   };

   return { deleteNode };
};

export default useDeleteNode;
